/**
 * SpiceBandToggle: SPICE | Friendly Visitor, the session type of a SPICE
 * note, picked in one place: the record's composer, for a seat offered
 * only the two SPICE bands. The SPICE doors (Log Session Note, the
 * decline form) hand the note to that composer and pick nothing. It sets
 * the note's rate band to 'spice' or 'spice_friendly_visitor', with the
 * schedule's own words for the two kinds (SPICE_KIND_LABELS).
 *
 * Drawn by SegmentedToggle: `${testIdPrefix}` is the radiogroup,
 * `${testIdPrefix}-spice` and `${testIdPrefix}-friendly-visitor` the
 * radios.
 *
 * useSpiceBandDefault (src/lib/spiceBandDefault.ts) gives the band a
 * visit starts on: the client's own session kind.
 */

import { type ReactNode } from 'react';
import { SegmentedToggle, type SegmentedOption } from '@/components/primitives/SegmentedToggle';
import { SPICE_KIND_LABELS } from '@/lib/spiceScheduleCopy';
import {
  SPICE_BAND,
  SPICE_FRIENDLY_VISITOR_BAND,
  type SpiceVisitRateBand,
} from '@/lib/spiceVisitNote';

const OPTIONS: readonly [SegmentedOption<SpiceVisitRateBand>, SegmentedOption<SpiceVisitRateBand>] =
  [
    { value: SPICE_BAND, label: SPICE_KIND_LABELS.spice, suffix: 'spice' },
    {
      value: SPICE_FRIENDLY_VISITOR_BAND,
      label: SPICE_KIND_LABELS.spice_friendly_visitor,
      suffix: 'friendly-visitor',
    },
  ];

export interface SpiceBandToggleProps {
  value: string;
  onChange: (next: string) => void;
  /** One SPICE-family per-visit band, drawn as a third segment
   *  after the two session kinds (its testid suffix is its band value). */
  extra?: { value: string; label: string } | null;
  testIdPrefix: string;
  /** The id of the host's visible label; the group's accessible name. */
  labelledBy?: string;
  /** Every segment shown, faded and unchoosable (a note whose pay period
   *  has closed). */
  disabled?: boolean;
}

export function SpiceBandToggle({
  value,
  onChange,
  testIdPrefix,
  labelledBy,
  extra = null,
  disabled = false,
}: SpiceBandToggleProps): ReactNode {
  const drawn: readonly [SegmentedOption<string>, SegmentedOption<string>] | readonly [SegmentedOption<string>, SegmentedOption<string>, SegmentedOption<string>] =
    extra === null ? OPTIONS : [OPTIONS[0], OPTIONS[1], { value: extra.value, label: extra.label, suffix: extra.value }];
  const options = disabled ? (drawn.map((o) => ({ ...o, disabled: true })) as unknown as typeof drawn) : drawn;
  return (
    <SegmentedToggle
      options={options}
      value={value}
      onChange={onChange}
      name="SPICE or Friendly Visitor"
      labelledBy={labelledBy}
      testIdPrefix={testIdPrefix}
    />
  );
}
